import { Alert, Button, Chip, lighten, useTheme } from '@mui/material'
import { AlertColor } from '@mui/material/Alert/Alert'
import { isEmpty } from 'lodash'
import PropTypes from 'prop-types'
import React, { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import agent from 'agent'
import ConfirmationModal from 'components/common/ConfirmationModal'
import Text, { styles as textStyles } from 'components/common/Text'
import TextArea from 'components/common/TextArea'
import Icon from 'components/Icon'
import HaltAllAttacksButton from 'components/navigation/HaltAllAttacksButton'
import MainHeaderMenu from 'components/navigation/MainHeaderMenu'
import { NotificationsMenu } from 'components/navigation/Notifications/NotificationsMenu'
import useAuth from 'hooks/use-auth'
import toast from 'lib/toast'
import theme from 'theme'
import { track } from 'track'
import { ContractTier } from 'types/api/companies/contracts'
import { pluralize } from 'utils/strings'

function HaltAllConfirmation({ open, onCancel }) {
  const { currentTeamId } = useAuth()
  const [reason, setReason] = useState('')

  return (
    <ConfirmationModal
      confirmText="Confirm"
      message={
        <TextArea
          data-test-input="reason"
          name="reason"
          placeholder="Optional Reason"
          value={reason}
          onChange={({ target: { value } }) => setReason(value)}
        />
      }
      open={open}
      title="Halt All Tests"
      onCancel={onCancel}
      onConfirm={async () => {
        try {
          await agent.post(`/halts?teamId=${currentTeamId}`, { reason })
          toast({
            text: 'Attacks successfully halted.',
            type: 'success'
          })

          track('HaltAllAttacks')
        } catch (e) {
          toast({
            type: 'error',
            text: 'There was an issue halting all attacks. Please try again later.'
          })
        }

        onCancel()
      }}
    />
  )
}

HaltAllConfirmation.propTypes = {
  open: PropTypes.bool,
  onCancel: PropTypes.func
}

interface AlertDetail {
  shouldDisplay: boolean
  level?: AlertColor
  message?: string
  action?: string
  actionUrl?: string
}

const Header: React.FC = ({ children, ...rest }) => {
  const {
    contractExpirationImminent,
    daysUntilContractExpiration,
    paywallStatus,
    session,
    tier,
    isProductionEnvironment
  } = useAuth()

  const muiTheme = useTheme()
  const isATeamMember = session?.teams?.length > 0

  const alertDetails: AlertDetail = useMemo(() => {
    if (tier === ContractTier.Enterprise) {
      return {
        level: daysUntilContractExpiration > 5 ? 'error' : 'info',
        message: `Your contract will end in ${daysUntilContractExpiration}
            ${pluralize('day', daysUntilContractExpiration)}.`,
        action: 'Contact Sales',
        actionUrl: 'https://www.gremlin.com/contact/',
        shouldDisplay: Boolean(contractExpirationImminent)
      }
    }

    if (tier === ContractTier.RMSubscription) {
      return {
        level: paywallStatus?.level,
        message: paywallStatus?.message,
        shouldDisplay: !isEmpty(paywallStatus)
      }
    }

    return {
      shouldDisplay: false
    }
  }, [
    tier,
    daysUntilContractExpiration,
    contractExpirationImminent,
    paywallStatus
  ])

  return (
    <header
      css={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        position: 'sticky',
        top: 0,
        height: 'var(--header-height)',
        background: theme.colors.white[0],
        borderBottom: theme.border.base,
        marginTop: 0,
        marginRight: 0,
        marginBottom: 0,
        zIndex: theme.zIndices[4],

        [theme.mq[2]]: {
          top: 0
        },
        ...(isProductionEnvironment
          ? {
              backgroundColor: lighten(muiTheme.palette.warning.light, 0.88)
            }
          : {})
      }}
      {...rest}
    >
      <div
        css={{
          display: 'flex',
          alignItems: 'center',
          padding: `20px ${theme.space[4]}px 19px ${theme.space[4]}px`,

          [theme.mq[2]]: {
            paddingRight: theme.space[7],
            paddingLeft: theme.space[7]
          }
        }}
      >
        {children}
        {isProductionEnvironment && (
          <Chip
            color="warning"
            data-testid="service-list-production-chip"
            label="Production"
            sx={{ marginLeft: 1 }}
          />
        )}
        <div
          css={{
            display: 'flex',
            alignItems: 'center',
            marginLeft: 'auto'
          }}
        >
          {alertDetails?.shouldDisplay && (
            <Alert
              data-test-header-alert
              action={
                alertDetails.action && (
                  <Button href={alertDetails.actionUrl} size="small">
                    {alertDetails.action}
                  </Button>
                )
              }
              severity={alertDetails.level}
            >
              {alertDetails.message}
            </Alert>
          )}

          {isATeamMember && <NotificationsMenu />}
          <MainHeaderMenu />

          {/* TODO: remove once the flag is lifted */}
          {isATeamMember && (
            <div css={{ marginLeft: theme.space[3] }}>
              <HaltAllAttacksButton />
            </div>
          )}
        </div>
      </div>
    </header>
  )
}

export default Header

export const HeaderLink: React.FC<{ to: string }> = ({
  to,
  children,
  ...rest
}) => {
  return (
    <Link to={to} {...rest} css={{ textDecoration: 'none' }}>
      <Text
        as="h3"
        color="normal"
        css={{
          '&:hover': {
            color: theme.colors.blue[0]
          }
        }}
      >
        {children}
      </Text>
    </Link>
  )
}

// @NOTE We want to use H1 for accessibility but use the styles of an H3
export const Heading: React.FC = (props) => {
  return (
    <Text as="h1" css={textStyles['h3']} data-testid="page-title" {...props} />
  )
}

export const HeaderRightChevron: React.FC = (props) => {
  return (
    <Icon
      css={{
        marginLeft: theme.space[2],
        marginRight: theme.space[2],
        flex: 'none' // Prevent collapsing
      }}
      icon="chevronRight"
      {...props}
    />
  )
}
